import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';

import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

import { BackendResponseModel } from '../../../http/models/backend-response.model';
import { mwCreateApiEffect } from '../../../store/effect.utils';
import { ROLE_SUPER_ADMIN, ROLE_SYSTEM_ADMIN } from '../../models';
import { MwAuthService } from '../../services/auth.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { MwUserApiService } from '../../services/user-api.service';
import { MwMonitorService } from './../../../error-handler/monitor.service';
import { ErrorCodes } from './../../../http/models/enums/error-codes.enum';
import { CompanyViewModel } from './../../models/company-view.model';
// App
import * as userActions from './user.actions';
import { MwUserEffectsService } from './user.effects.service';

@Injectable()
export class UserEffects implements OnInitEffects {
  init$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userActions.init),
        tap(() => {
          this.userEffectsService.registerFcmToken();
        })
      ),
    { dispatch: false }
  );

  trySetActiveCompany = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.trySetActiveCompany),
      switchMap((action) =>
        this.userApiService.getCompanies().pipe(
          map((companies) => {
            if (companies.length === 0) {
              var error = new BackendResponseModel();
              error.addError({
                errorCode: ErrorCodes.Forbidden,
                errorDescription: 'User has no access to any company',
              });

              throw error;
            }
            return this.getCompanyData({ companies, ...action });
          }),
          switchMap((data) => {
            const result = {
              ...data,
              companyNotRequired: action.companyNotRequired,
            };
            if (data.couldSetCompany || action.companyNotRequired) {
              return of(result);
            }

            // If user is system admin we shouldn't force
            // to select company
            return this.userApiService.getProfile().pipe(
              map((profile) => ({
                ...result,
                companyNotRequired:
                  profile.roles.indexOf(ROLE_SYSTEM_ADMIN) !== -1 ||
                  profile.roles.indexOf(ROLE_SUPER_ADMIN) !== -1,
              }))
            );
          }),
          switchMap(
            ({
              couldSetCompany,
              activeCompany,
              hasManyCompanies,
              companyNotRequired,
            }) => {
              const mainAction =
                couldSetCompany || companyNotRequired
                  ? userActions.setActiveCompany({
                      company: activeCompany,
                      language: action.selectedLanguage,
                      hasManyCompanies,
                    })
                  : userActions.changeCompany({
                      selectedCompanyId: undefined,
                      // If user is forced to pick company - we should save current route
                      // in order to return to previous point.
                      isSaveCurrentRoute: true,
                    });

              return [
                mainAction,
                userActions.setHasManyCompanies({ hasManyCompanies }),
              ];
            }
          ),
          catchError((err) =>
            of(
              userActions.setActiveCompanyFailed({
                err,
                rawUserData: action.rawUserData,
              })
            )
          )
        )
      )
    )
  );

  setActiveCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.setActiveCompany),
      map((action) =>
        userActions.requestUserProfile({
          companyId: action.company?.companyId,
          language: action.language,
        })
      ),
      tap((action) =>
        LocalStorageService.setLastActiveCompanyId(action.companyId || 0)
      )
    )
  );

  requestUserProfile$ = mwCreateApiEffect(
    this.actions$,
    userActions.requestUserProfile,
    (action) => this.userApiService.getProfile(action.companyId),
    (profile, action) => {
      return userActions.setUserProfile({
        userApiProfile: profile,
        forceLanguage: action.language,
      });
    },
    () =>
      userActions.setUserProfile({
        userApiProfile: null,
        forceLanguage: undefined,
      })
  );

  trySetActiveCompanyFailed = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userActions.setActiveCompanyFailed),
        tap((err) => {
          const isBackendError = err.err instanceof BackendResponseModel;
          if (
            err.err instanceof BackendResponseModel &&
            err.err.errors.some(
              (t) =>
                [ErrorCodes.Unauthorized, ErrorCodes.Forbidden].indexOf(
                  t.errorCode
                ) !== -1
            )
          ) {
            this.router.navigate(['no-access']);
          } else {
            const errorRouteExists = this.router.config.find(
              (t) => t.path === 'error'
            );
            const backendError = err.err as BackendResponseModel;
            const error = isBackendError
              ? {
                  name: 'Unable to set active company',
                  message: backendError.errors[0].toString(),
                }
              : err.err;

            console.error(err.err);
            this.monitorService.logError(error, err.rawUserData);
            if (errorRouteExists) {
              this.router.navigate(['error']);
            } else {
              this.router.navigate(['no-access']);
            }
          }
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userActions.logOut),
        tap(() => {
          this.authService.logoff().subscribe((result) => console.log(result));
        })
      ),
    { dispatch: false }
  );

  registerFcmToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userActions.registerFcmToken),
        tap((action) => {
          this.userApiService.registerFcmToken(action.token).subscribe();
        })
      ),
    { dispatch: false }
  );

  changeCompany = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userActions.changeCompany),
        tap((action) => {
          this.authService.changeCompany(action.selectedCompanyId, {
            isSaveCurrentRoute: action.isSaveCurrentRoute,
          });
        })
      ),
    { dispatch: false }
  );

  changeLanguage = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.changeLanguage),
      switchMap((action) =>
        this.userApiService.changeLanguage(action.userLanguage).pipe(
          map(() =>
            userActions.userProfileLanguageChanged({
              userLanguage: action.userLanguage,
            })
          ),
          catchError((err) =>
            of(userActions.userProfileLanguageChangeFailed({ err }))
          )
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly userApiService: MwUserApiService,
    private readonly userEffectsService: MwUserEffectsService,
    private readonly router: Router,
    private readonly authService: MwAuthService,
    private readonly monitorService: MwMonitorService
  ) {}

  ngrxOnInitEffects(): Action {
    this.subscribeToAutoLoginRoute();
    return userActions.init();
  }

  private subscribeToAutoLoginRoute(): void {
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart &&
            event.url.indexOf('?autologin=') !== -1
        )
      )
      .subscribe(() => {
        const guid = this.getAutoLoginGuid();

        if (guid) {
          this.authService.authorizeByGuid(guid);
        }
      });
  }

  private getAutoLoginGuid(): string | null {
    return new URLSearchParams('?' + window.location.href.split('?')[1]).get(
      'autologin'
    );
  }

  private getCompanyData(data: {
    lastActiveCompanyId: number | null;
    companies: CompanyViewModel[];
    selectedCompanyId: number;
    companyNotRequired: boolean;
  }): {
    activeCompany: CompanyViewModel | null;
    hasManyCompanies: boolean;
    couldSetCompany: boolean;
  } {
    const activeCompany = this.authService.isUseSTSCompanySelector()
      ? data.companies.find((t) => t.companyId === data.selectedCompanyId)
      : data.companies.find((t) => t.companyId === data.lastActiveCompanyId) ||
        data.companies.find((t) => t.companyId === data.selectedCompanyId);

    const hasManyCompanies = data.companies.length > 1;
    const couldSetCompany =
      data.companies.length === 0 || !!activeCompany || data.companyNotRequired;

    return {
      activeCompany: activeCompany || null,
      hasManyCompanies,
      couldSetCompany,
    };
  }
}
